"use client";

import { motion } from "framer-motion";
import { PlayerAvatar } from "@/components/shared/player-avatar";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import type { VysledokHraca } from "@/types/database";
import {
  getHracId,
  getHracMeno,
  getPozicia,
  getPocetHlasov,
} from "@/types/database";

interface RosterRinkProps {
  vysledky: VysledokHraca[];
}

interface PlayerNodeProps {
  hrac?: VysledokHraca;
  pozicia: string;
}

const PLAYER_NODE_BASE = cn(
  "flex flex-col items-center",
  "gap-1.5 sm:gap-2 md:gap-2.5",
);
const EMPTY_NODE_CONTAINER = cn(PLAYER_NODE_BASE, "opacity-30");
const EMPTY_NODE_CIRCLE = cn(
  "w-12 h-12 sm:w-14 md:w-16 rounded-full",
  "border-2 border-dashed border-primary-foreground/40",
  "bg-primary-foreground/10 flex items-center justify-center",
  "transition-all hover:bg-primary-foreground/20",
);

const BADGE_CONTAINER_CLASSES = cn(
  "absolute -bottom-1 -right-1",
  "bg-hockey-gold text-hockey-gold-foreground",
  "text-xs font-bold px-1.5 py-0.5 rounded shadow-sm",
  "border border-primary-foreground",
);
const PLAYER_NAME_CONTAINER_CLASSES = cn(
  "bg-background/80 dark:bg-card/70 backdrop-blur-sm",
  "px-2 py-0.5 rounded shadow-lg border border-primary-foreground/10",
);
const RINK_CARD_CLASSES = cn(
  "relative w-full max-w-4xl mx-auto overflow-hidden",
  "border-4 border-sidebar-border shadow-2xl",
  "bg-linear-to-b from-sidebar to-sidebar-accent",
);

const PlayerNode = ({ hrac, pozicia }: PlayerNodeProps) => {
  if (!hrac) {
    return (
      <div className={EMPTY_NODE_CONTAINER}>
        <div className={EMPTY_NODE_CIRCLE}>
          <span className="text-xs text-primary-foreground/70 font-bold">
            {pozicia}
          </span>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ delay: 0.1 }}
      className={cn(PLAYER_NODE_BASE, "z-10 sm:gap-2")}
    >
      <div className="relative">
        <PlayerAvatar
          hracId={getHracId(hrac)}
          meno={getHracMeno(hrac)}
          pozicia={getPozicia(hrac)}
          className={cn(
            "w-12 h-12 sm:w-14 md:w-16",
            "border-2 border-primary-foreground",
            "shadow-lg ring-2 ring-primary/40",
            "hover:ring-primary transition-all",
          )}
          fallbackClassName="text-xs"
        />
        <div className={BADGE_CONTAINER_CLASSES}>{getPocetHlasov(hrac)}</div>
      </div>
      <div className={PLAYER_NAME_CONTAINER_CLASSES}>
        <span className="text-xs font-bold text-foreground whitespace-nowrap">
          {getHracMeno(hrac).split(" ").pop()}
        </span>
      </div>
    </motion.div>
  );
};

export function RosterRink({ vysledky }: RosterRinkProps) {
  const brankar = vysledky
    .filter((v) => getPozicia(v) === "brankar")
    .sort((a, b) => getPocetHlasov(b) - getPocetHlasov(a))[0];

  const obrancovia = vysledky
    .filter((v) => getPozicia(v) === "obranca")
    .sort((a, b) => getPocetHlasov(b) - getPocetHlasov(a))
    .slice(0, 2);

  const utocnici = vysledky
    .filter((v) => getPozicia(v) === "utocnik")
    .sort((a, b) => getPocetHlasov(b) - getPocetHlasov(a))
    .slice(0, 3);

  return (
    <Card className={RINK_CARD_CLASSES}>
      <div className="relative w-full aspect-4/3 bg-linear-to-b from-primary/5 to-primary/10">
        <svg
          className="absolute inset-0 w-full h-full"
          viewBox="0 0 400 300"
          fill="none"
          preserveAspectRatio="xMidYMid meet"
        >
          <defs>
            <pattern
              id="ice-texture"
              x="0"
              y="0"
              width="20"
              height="20"
              patternUnits="userSpaceOnUse"
            >
              <circle
                cx="10"
                cy="10"
                r="0.5"
                fill="currentColor"
                className="text-rink-ice-pattern"
              />
            </pattern>
          </defs>

          <rect x="0" y="0" width="400" height="300" fill="url(#ice-texture)" />

          <rect
            x="10"
            y="10"
            width="380"
            height="280"
            rx="30"
            stroke="currentColor"
            className="text-rink-line-red"
            strokeWidth="3"
            fill="none"
          />

          <line
            x1="200"
            y1="10"
            x2="200"
            y2="290"
            stroke="currentColor"
            className="text-rink-line-red"
            strokeWidth="2"
          />

          <line
            x1="100"
            y1="10"
            x2="100"
            y2="290"
            stroke="currentColor"
            className="text-rink-line-blue"
            strokeWidth="2"
          />
          <line
            x1="300"
            y1="10"
            x2="300"
            y2="290"
            stroke="currentColor"
            className="text-rink-line-blue"
            strokeWidth="2"
          />

          <circle
            cx="200"
            cy="150"
            r="30"
            stroke="currentColor"
            className="text-rink-line-blue"
            strokeWidth="2"
            fill="none"
          />
          <circle
            cx="200"
            cy="150"
            r="2"
            fill="currentColor"
            className="text-rink-line-blue"
          />

          <circle
            cx="80"
            cy="100"
            r="3"
            fill="currentColor"
            className="text-rink-line-red"
          />
          <circle
            cx="80"
            cy="200"
            r="3"
            fill="currentColor"
            className="text-rink-line-red"
          />
          <circle
            cx="320"
            cy="100"
            r="3"
            fill="currentColor"
            className="text-rink-line-red"
          />
          <circle
            cx="320"
            cy="200"
            r="3"
            fill="currentColor"
            className="text-rink-line-red"
          />

          <path
            d="M 10 130 L 30 130 Q 40 150 30 170 L 10 170"
            stroke="currentColor"
            className="text-rink-line-blue"
            strokeWidth="2"
            fill="none"
          />
          <path
            d="M 390 130 L 370 130 Q 360 150 370 170 L 390 170"
            stroke="currentColor"
            className="text-rink-line-blue"
            strokeWidth="2"
            fill="none"
          />
        </svg>

        <div className="absolute inset-0 flex flex-col justify-between p-8 md:p-12">
          <div className="flex justify-around items-center">
            <PlayerNode hrac={utocnici[0]} pozicia="LW" />
            <PlayerNode hrac={utocnici[1]} pozicia="C" />
            <PlayerNode hrac={utocnici[2]} pozicia="RW" />
          </div>

          <div className="flex justify-center gap-24 md:gap-40 items-center">
            <PlayerNode hrac={obrancovia[0]} pozicia="LD" />
            <PlayerNode hrac={obrancovia[1]} pozicia="RD" />
          </div>

          <div className="flex justify-center">
            <PlayerNode hrac={brankar} pozicia="G" />
          </div>
        </div>

        <div className="absolute bottom-3 left-4 text-xs text-primary-foreground/50 font-mono uppercase tracking-widest">
          Top Fan Selection
        </div>
        <div className="absolute top-3 right-4 text-xs text-primary-foreground/50 font-mono uppercase tracking-widest">
          ZOH 2026
        </div>
      </div>
    </Card>
  );
}
